import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, ScrollRestoration, useNavigate, useParams } from 'react-router';

import {
  ORDER_STAGES,
  stageToStep,
  type OrderStage,
  type StepNumber,
  type TrackerStepState,
} from '@entities/search-session';
import { GuestBanner } from '@features/guest';
import { ROUTES } from '@shared/config/routes';
import {
  AppShellContext,
  EMPTY_SLOTS,
  type AppShellSlots,
  type ShellNextStep,
} from '@shared/lib/appShell';
import { Button, ButtonLink, Container } from '@shared/ui';

import { AppHeader } from './AppHeader';
import styles from './AppLayout.module.css';
import { ContextRail } from './ContextRail';
import { Footer } from './Footer';
import { focusKarlInput } from './focusKarlInput';
import { KarlRail, KarlRailMobile, type KarlOpenRequest } from './KarlRail';

/** Стадия из слота приходит строкой: сужаем её на границе оболочки. */
function asStage(value: string | null): OrderStage | null {
  return value !== null && (ORDER_STAGES as readonly string[]).includes(value)
    ? (value as OrderStage)
    : null;
}

/** Шаг четвёрки: с него рабочий набор уводит оплаченный заказ на Top-4. */
const SELECTION_STEP = 4;

/**
 * Где сейчас живёт диалог Karl — те же границы, что в `AppLayout.module.css`:
 * с 1024 px — постоянный рельс, уже — пузырь с листом; ниже 768 px при
 * переходе к следующему шагу пузырь стоит в полосе (FE-585).
 */
const KARL_RAIL_QUERY = '(min-width: 1024px)';
const KARL_DOCK_QUERY = '(max-width: 767px)';

/** Какой из двух пузырей открывает лист: плавающий или в полосе телефона. */
interface KarlSheetRequest extends KarlOpenRequest {
  target: 'floating' | 'docked';
}

/** Что оболочка знает о заказе, чтобы выбрать экран шага. */
interface StepContext {
  searchId: string;
  paid: boolean;
  /** Текущий шаг заказа; `null` — стадия неизвестна. */
  currentStep: StepNumber | null;
  /** Переписка выбранного автомобиля — если её передал экран. */
  negotiationId: string | null;
}

/**
 * Экран каждого шага трекера: клик по пройденному шагу ведёт сюда
 * (DESIGN_SYSTEM §5.3). Будущий и закрытый оплатой шаг не нажимаются — это
 * решает сам трекер (`TrackerStepState.navigable`). `null` — у шага экрана
 * нет, и он остаётся статичным, даже будучи пройденным.
 *
 * Три строки зависят от заказа:
 *
 * * **шаг 2 у оплаченного заказа** ведёт на рабочий набор, а не на экран
 *   анализа: тот сам уводит оплаченный заказ на четвёрку, и клик по «Analyse»
 *   приводил бы на шаг 4. Когда заказ уже на шаге 4 и дальше, туда же уводит и
 *   рабочий набор (`ResultsPage`), поэтому шаг 2 становится статичным. После
 *   правки критериев оплаченный заказ возвращается на шаг 2 — тогда рабочий
 *   набор открыт, и ссылка снова есть;
 * * **шаг 3 у оплаченного заказа** экрана не имеет: квитанции нет, а экран
 *   оплаты тоже уводит на четвёрку. До оплаты он ведёт на оплату, как прежде;
 * * **шаг 5** ведёт в переписку выбранного автомобиля, если экран передал её
 *   идентификатор (`AppShellSlots.negotiationId`), иначе — в список переписок.
 *   Отдельного запроса ради одной ссылки в шапке оболочка не делает.
 */
function stepTarget(
  step: StepNumber,
  { searchId, paid, currentStep, negotiationId }: StepContext,
): string | null {
  switch (step) {
    case 1:
      return ROUTES.searchAssistant(searchId);
    case 2:
      if (!paid) return ROUTES.searchAnalysis(searchId);
      return currentStep !== null && currentStep >= SELECTION_STEP
        ? null
        : ROUTES.searchResults(searchId);
    case 3:
      return paid ? null : ROUTES.searchPayment(searchId);
    case 4:
      return ROUTES.searchTop4(searchId);
    case 5:
      return negotiationId === null ? ROUTES.negotiations : ROUTES.negotiation(negotiationId);
    case 6:
      return ROUTES.searchConclusion(searchId);
    case 7:
      return ROUTES.searchHandover(searchId);
  }
}

/**
 * Оболочка `/app/*` (FE-192, DESIGN_SYSTEM §4.3).
 *
 * Три зоны: узкий рельс Karl, широкий центр, узкий контекстный сайдбар —
 * поверх тёмная шапка с трекером семи шагов. Раскладка — CSS Grid; `calc()`
 * с процентами и флексбоксовая арифметика ширин запрещены.
 *
 * **Страница не рисует навигацию и не знает о шагах.** Она сообщает оболочке
 * стадию и содержимое рельсов через `useAppShell`, а всё остальное — работа
 * этого слоя. Экраны вне воронки (список заказов) стадию не передают: трекер
 * тогда не рисуется вовсе, потому что показывать шаг «какого-то» заказа было
 * бы враньём о состоянии.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const [slots, setSlots] = useState<AppShellSlots>(EMPTY_SLOTS);
  const [nextStep, setNextStep] = useState<ShellNextStep | null>(null);
  const [karlSheet, setKarlSheet] = useState<KarlSheetRequest | null>(null);
  const karlColumnRef = useRef<HTMLDivElement>(null);
  // Переход к следующему шагу читается по ссылке: `focusKarl` обязан быть
  // стабильным, а от полосы зависит, какой пузырь открывать.
  const hasNextStepRef = useRef(false);
  useEffect(() => {
    hasNextStepRef.current = nextStep !== null;
  }, [nextStep]);

  // Ссылка на контекст создаётся один раз: сеттеры из `useState` стабильны,
  // а меняющееся значение контекста зациклило бы эффект в `useAppShell`.
  const store = useMemo(
    () => ({
      setSlots,
      setNextStep,
      // «Mit Karl sprechen» (FE-586): экран не знает, где сейчас диалог. С
      // 1024 px он в рельсе — туда ставится фокус; уже — открывается лист того
      // пузыря, который сейчас виден, и фокус встаёт в поле внутри листа.
      // «Suche anpassen» (FE-587) приносит ещё и начало сообщения: оно встаёт
      // в пустое поле, черновик человека не трогается.
      focusKarl: (prefill?: string) => {
        if (window.matchMedia(KARL_RAIL_QUERY).matches) {
          focusKarlInput(karlColumnRef.current, prefill);
          return;
        }
        const docked = hasNextStepRef.current && window.matchMedia(KARL_DOCK_QUERY).matches;
        setKarlSheet((previous) => ({
          target: docked ? 'docked' : 'floating',
          id: (previous?.id ?? 0) + 1,
          prefill,
        }));
      },
    }),
    [],
  );

  const stage = asStage(slots.stage);
  const hasContext = slots.context !== null;

  // Karl сопровождает весь путь, и наполнение рельса задаёт экран (FE-193).
  //
  // **Исключение ровно одно: `karl === null`.** Так экран говорит «диалог с
  // Karl — это и есть моё содержимое», и рельс не рисуется. Иначе на экране
  // диалога получались бы две ленты и два поля ввода для одной переписки —
  // дубль основного действия (DESIGN_SYSTEM §5.1) и лишняя колонка, которая
  // забирает ширину у настоящего диалога.
  const karl = slots.karl;

  // Заказ, к экранам которого ведут шаги, — из адреса экрана
  // (`/app/searches/:searchId/...`): параметры дочернего маршрута видны и
  // оболочке. В адресе переписки (`/app/negotiations/:id`) заказа нет — его
  // передаёт сама карточка из данных переписки (`AppShellSlots.searchId`).
  // Нет ни там, ни там — трекер остаётся индикатором без переходов: кнопка,
  // которой некуда вести, хуже отсутствия кнопки.
  const params = useParams();
  const searchId = params.searchId ?? slots.searchId ?? undefined;
  const navigate = useNavigate();
  const paid = slots.paid;
  const negotiationId = slots.negotiationId ?? null;
  const currentStep = stage === null ? null : stageToStep(stage);
  const tracker = useMemo(() => {
    if (searchId === undefined) return { onNavigate: undefined, canNavigate: undefined };
    const context: StepContext = { searchId, paid, currentStep, negotiationId };
    return {
      onNavigate: (step: TrackerStepState) => {
        const target = stepTarget(step.number, context);
        if (target !== null) void navigate(target);
      },
      canNavigate: (step: TrackerStepState) => stepTarget(step.number, context) !== null,
    };
  }, [navigate, searchId, paid, currentStep, negotiationId]);

  return (
    <AppShellContext.Provider value={store}>
      <div className={styles.shell}>
        <a className="skip-link" href="#main">
          {t('nav.skipToContent')}
        </a>

        {stage === null ? (
          <AppHeader stage="draft" paid={slots.paid} showTracker={false} />
        ) : (
          <AppHeader
            stage={stage}
            paid={slots.paid}
            onNavigate={tracker.onNavigate}
            canNavigate={tracker.canNavigate}
          />
        )}

        <div className={styles.body}>
          <Container
            width="app"
            as="div"
            className={[
              styles.grid,
              karl && styles.withKarl,
              hasContext && styles.withContext,
              nextStep && styles.withDock,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {karl ? (
              <>
                <div className={styles.karlColumn} ref={karlColumnRef}>
                  <KarlRail {...karl} />
                </div>
                {/* Плавающий пузырь — до 1024 px. На телефоне при переходе к
                  следующему шагу тот же пузырь переезжает в полосу внизу. */}
                <div className={nextStep ? styles.floatingKarlAboveDock : styles.floatingKarl}>
                  <KarlRailMobile
                    {...karl}
                    openRequest={karlSheet?.target === 'floating' ? karlSheet : undefined}
                  />
                </div>
              </>
            ) : null}

            <main id="main" className={styles.main}>
              {/* Гостевой режим объявлен один раз на оболочку, а не на каждом
                экране: обещание «сохранено в этом браузере» и способ его
                сохранить обязаны быть видны везде (FE-205). У вошедшего
                пользователя полоса не рисуется вовсе. */}
              <GuestBanner />
              <Outlet />
            </main>

            {hasContext ? (
              <div className={styles.contextColumn}>
                <ContextRail label={slots.contextLabel || t('app.context.label')}>
                  {slots.context}
                </ContextRail>
              </div>
            ) : null}
          </Container>

          {/* Полоса телефона (FE-585, DESIGN_SYSTEM §4.4): пузырь Karl и переход
            к следующему шагу — одной строкой у нижнего края. Полоса липкая, а
            не фиксированная: она занимает своё место в потоке, поэтому
            перенесённая на две строки подпись ничего не перекрывает, а у
            конца содержимого полоса останавливается и не ложится на подвал с
            Impressum. С 768 px её нет — переход стоит в потоке экрана. */}
          {nextStep ? (
            <div className={styles.dock}>
              {karl ? (
                <KarlRailMobile
                  {...karl}
                  docked
                  openRequest={karlSheet?.target === 'docked' ? karlSheet : undefined}
                />
              ) : null}
              {nextStep.to === undefined ? (
                <Button size="lg" className={styles.dockAction} onClick={nextStep.onClick}>
                  {nextStep.label}
                </Button>
              ) : (
                <ButtonLink to={nextStep.to} size="lg" className={styles.dockAction}>
                  {nextStep.label}
                </ButtonLink>
              )}
            </div>
          ) : null}
        </div>

        {/* Тот же подвал, что и на публичных страницах. Отдельная правовая
            полоса для `/app/*` держала те же ссылки, но выглядела иначе, — а
            подвал в продукте один. Обязанности он закрывает те же: Impressum
            «ständig verfügbar» (§ 5 DDG) и доступная функция отзыва
            (§ 356a Abs. 1 BGB), причём платит человек именно здесь. */}
        <Footer />

        <ScrollRestoration />
      </div>
    </AppShellContext.Provider>
  );
}
